/**
 * `<SettingsSurface>` — the Settings surface root (frontend.md §1.3 Tier-1, §2.1).
 *
 * Three sub-tabs (UX-reconciliation IA contraction, 2026-05-16):
 *   - **General** — remastered U4 (2026-07-04) into titled groups with a sticky
 *     in-page section rail:
 *       Appearance          theme · custom themes · UI scale · editor font
 *       Identity            owner display name · owner entity
 *       Capture & Routing   inbox address · autorouter
 *       Integrations        MCP
 *       Application         software updates · import
 *   - **Sources** — embeds `<SourceManagementSurface embedded />`.
 *   - **Health** — embeds `<CorpusHealthSurface embedded />`.
 *
 * The active tab is URL state via `?tab=` (frontend.md §2.2); `?section=` deep-links
 * a panel or group within General (existing contract — group ids g-appearance etc.
 * joined the map in U4).
 *
 * GATING (U4): the server settings query (`useSettingsQuery`) gates ONLY the panels
 * that actually read/write it (Identity, Autorouter). Client-only panels (theme,
 * scale, fonts, updates) and self-fetching panels (inbox address, MCP) render even
 * when the backend is unreachable — previously one failed query blanked the whole tab.
 */

import { useEffect, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useSettingsQuery, useUpdateSettingsMutation } from '@/hooks';
import { LoadingState, EmptyState } from '@/components';
import { OwnerUserNameSettings } from './OwnerUserNameSettings';
import { AutorouterSettings } from './AutorouterSettings';
import { OwnerEntitySettings } from './OwnerEntitySettings';
import { McpIntegration } from './McpIntegration';
import { InboxAddressSettings } from './InboxAddressSettings';
import { ThemeSettings } from './ThemeSettings';
import { CustomThemesSettings } from './CustomThemesSettings';
import { UiScaleSettings } from './UiScaleSettings';
import { EditorFontSettings } from './EditorFontSettings';
import { SoftwareUpdateSettings } from './SoftwareUpdateSettings';
import { SourceManagementSurface } from '@/surfaces/sources/SourceManagementSurface';
import { CorpusHealthSurface } from '@/surfaces/health/CorpusHealthSurface';
import { ImportPanel } from './ImportPanel';
// U4: settings-panel layout primitives (.settings-row / .settings-stack /
// .settings-hint …) — imported once here for every panel in this directory.
// NOTE: the surface header below intentionally stays hand-rolled .page-head
// markup; PageHead adoption is out of U4's scope.
import './settings.css';

type SettingsTab = 'general' | 'sources' | 'import' | 'health';

// `import` is intentionally NOT listed in the visible tab bar — Import is
// surfaced via the TopBar burger menu (Phase Cl-1e) + the Application group.
const TABS: { id: SettingsTab; label: string }[] = [
  { id: 'general', label: 'General' },
  { id: 'sources', label: 'Sources' },
  { id: 'health', label: 'Pool Health' },
];

const DEFAULT_TAB: SettingsTab = 'general';

/** The General tab's section rail — one entry per titled group. */
const GROUPS: { id: string; label: string }[] = [
  { id: 'g-appearance', label: 'Appearance' },
  { id: 'g-identity', label: 'Identity' },
  { id: 'g-capture', label: 'Capture & Routing' },
  { id: 'g-integrations', label: 'Integrations' },
  { id: 'g-application', label: 'Application' },
];

function parseTab(value: string | null): SettingsTab {
  if (value === 'general' || value === 'sources' || value === 'import' || value === 'health') return value;
  return DEFAULT_TAB;
}

export function SettingsSurface(): JSX.Element {
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = useMemo(() => parseTab(searchParams.get('tab')), [searchParams]);

  const settingsQuery = useSettingsQuery();
  const updateMutation = useUpdateSettingsMutation();

  // `?section=` deep-link (frontend.md §2.3). Legacy panel ids keep working;
  // U4 adds the group ids.
  const sectionAnchor = searchParams.get('section');
  useEffect(() => {
    if (tab !== 'general' || !sectionAnchor) return;
    const anchorMap: Record<string, string> = {
      writeback: 'write-back',
      'api-keys': 'api-keys',
      owner: 'owner-entity',
      cost: 'cost',
      appearance: 'g-appearance',
      identity: 'g-identity',
      capture: 'g-capture',
      integrations: 'g-integrations',
      application: 'g-application',
    };
    const id = anchorMap[sectionAnchor] ?? sectionAnchor;
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [tab, sectionAnchor]);

  const onTabClick = (next: SettingsTab): void => {
    const params = new URLSearchParams(searchParams);
    if (next === DEFAULT_TAB) params.delete('tab');
    else params.set('tab', next);
    setSearchParams(params, { replace: false });
  };

  return (
    <div className="surface surface--settings">
      <div className="page-head">
        <div className="page-head__main">
          <div className="page-head__eyebrow">configuration</div>
          <h1 className="page-title">settings.</h1>
          <div className="page-sub">
            App configuration, source connectors, and corpus health observability.
          </div>
        </div>
      </div>

      <div className="settings-tabs" role="tablist" aria-label="Settings sections">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={tab === t.id}
            className="settings-tab"
            onClick={() => onTabClick(t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="settings-body">
        {tab === 'general' && (
          <GeneralTab
            isLoading={settingsQuery.isLoading}
            isError={settingsQuery.isError}
            settings={settingsQuery.data}
            saving={updateMutation.isPending}
            onUpdate={(patch) => updateMutation.mutate(patch as never)}
            onRetry={() => void settingsQuery.refetch()}
          />
        )}
        {tab === 'sources' && <SourceManagementSurface embedded active />}
        {tab === 'import' && <ImportPanel />}
        {tab === 'health' && <CorpusHealthSurface embedded active />}
      </div>
    </div>
  );
}

// ----------------------------------------------------------------------------
// General tab — titled groups + section rail (U4). Server-backed groups gate
// individually on the settings query; everything else always renders.
// ----------------------------------------------------------------------------

interface GeneralTabProps {
  isLoading: boolean;
  isError: boolean;
  settings: ReturnType<typeof useSettingsQuery>['data'];
  saving: boolean;
  onUpdate: (patch: Record<string, unknown>) => void;
  onRetry: () => void;
}

/** Wraps panels that need the server settings document. */
function ServerSettingsGate({
  isLoading,
  isError,
  ready,
  onRetry,
  children,
}: {
  isLoading: boolean;
  isError: boolean;
  ready: boolean;
  onRetry: () => void;
  children: React.ReactNode;
}): JSX.Element {
  if (isLoading) return <LoadingState label="Loading settings…" />;
  if (isError || !ready) {
    return (
      <EmptyState
        icon="warning"
        title="Server settings unavailable"
        detail="These panels read the backend settings document. Check the connection, then retry."
        action={
          <button type="button" className="btn btn--sm" onClick={onRetry}>
            Try again
          </button>
        }
      />
    );
  }
  return <>{children}</>;
}

function GeneralTab({
  isLoading,
  isError,
  settings,
  saving,
  onUpdate,
  onRetry,
}: GeneralTabProps): JSX.Element {
  const ready = settings !== undefined;
  const gate = { isLoading, isError, ready, onRetry };

  return (
    <div className="settings-layout">
      <nav className="settings-rail" aria-label="Settings sections">
        {GROUPS.map((g) => (
          <a key={g.id} className="settings-rail__link" href={`#${g.id}`}>
            {g.label}
          </a>
        ))}
      </nav>

      <div className="settings-sections">
        <section id="g-appearance" className="settings-group">
          <h2 className="settings-group__title">Appearance</h2>
          <div className="settings-grid">
            <ThemeSettings />
            <CustomThemesSettings />
            <UiScaleSettings />
            <EditorFontSettings />
          </div>
        </section>

        <section id="g-identity" className="settings-group">
          <h2 className="settings-group__title">Identity</h2>
          <ServerSettingsGate {...gate}>
            <div className="settings-grid">
              <OwnerUserNameSettings
                userName={
                  typeof settings?.values['owner_user_name'] === 'string' &&
                  (settings.values['owner_user_name'] as string).length > 0
                    ? (settings.values['owner_user_name'] as string)
                    : null
                }
                saving={saving}
                onSave={(userName) => onUpdate({ ownerUserName: userName })}
              />
              <OwnerEntitySettings
                ownerEntityId={settings?.ownerEntityId ?? null}
                saving={saving}
                onSave={(entityId) => onUpdate({ ownerEntityId: entityId })}
              />
            </div>
          </ServerSettingsGate>
        </section>

        <section id="g-capture" className="settings-group">
          <h2 className="settings-group__title">Capture &amp; Routing</h2>
          <div className="settings-grid">
            {/* Self-fetching — renders its own loading/error states. */}
            <InboxAddressSettings />
          </div>
          <ServerSettingsGate {...gate}>
            <div className="settings-grid">
              {settings !== undefined && (
                <AutorouterSettings settings={settings} saving={saving} onUpdate={onUpdate} />
              )}
            </div>
          </ServerSettingsGate>
        </section>

        <section id="g-integrations" className="settings-group">
          <h2 className="settings-group__title">Integrations</h2>
          <div className="settings-grid">
            {/* Self-fetching (mint / list / revoke) — independent of the settings doc. */}
            <McpIntegration />
          </div>
        </section>

        <section id="g-application" className="settings-group">
          <h2 className="settings-group__title">Application</h2>
          <div className="settings-grid">
            {/* Desktop-only; renders null on web. */}
            <SoftwareUpdateSettings />
          </div>
          <div className="settings-group__links">
            <Link className="btn btn--sm" to="/settings?tab=import">
              Import markdown files…
            </Link>
          </div>
        </section>
      </div>
    </div>
  );
}
